import React, {useCallback, useRef} from 'react';
import {FlatList, Platform, Pressable, StyleSheet, View} from 'react-native';
import {useHeaderHeight} from '@react-navigation/elements';

import LoadingImg from '../../components/LoadingImg';
import {ProductTileCarousel} from '../../components/ProductTile';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import {useScrollCollapseJSHandler} from '../../hooks/useScrollCollapse';
import {ScreenTypes, useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useWishlistProducts} from '../../queries/wishlist';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import {OrderLine} from '../../transport/apiServiceResponse';

const WishlistScreen = ({navigation}: RouteNavigatorScreenProps<AppRoute.WISHLIST>) => {
  const {userStore} = useStores();
  const {data: wishlistProducts, isLoading} = useWishlistProducts();
  const listRef = useRef(null);
  const {isWebDesktopScreen, screenType} = useWebScreenDimensions();
  const {scrollHandler} = useScrollCollapseJSHandler(listRef);
  const headerHeight = useHeaderHeight();

  let numColumns = 2;

  switch (screenType) {
    case ScreenTypes.mobile:
      numColumns = 2;
      break;
    case ScreenTypes.desktop:
      numColumns = 4;
      break;
    case ScreenTypes.desktopLarge:
      numColumns = 6;
      break;
  }

  const productTileAbbreviatedHorizontalWrapperWidthStyle = StyleSheet.create({
    main: {
      flex: 1 / numColumns,
    },
  });

  const renderItem = useCallback(
    ({item}: {item: OrderLine}) => (
      <View style={productTileAbbreviatedHorizontalWrapperWidthStyle.main}>
        <ProductTileCarousel item={item} fullWidth testContext="wishlist" />
      </View>
    ),
    [productTileAbbreviatedHorizontalWrapperWidthStyle.main],
  );

  if (!userStore.isLoggedIn) {
    return (
      <ContentView testID="discover-screen" style={{justifyContent: 'center'}}>
        <Pressable
          onPress={() => navigation.navigate(AppRoute.ACCOUNT_NOT_LOGGED_IN)}
          style={[styles.centerNotice, {marginTop: null}]}>
          <Text style={styles.largeNotice}>Please log in</Text>
          <Text style={styles.largeNotice}>to use wishlists</Text>
        </Pressable>
        {/* {!!isWebDesktopScreen && <WebFooter />} */}
      </ContentView>
    );
  } else {
    // Need to convert the observable to a plain data object because _getItem in flatlist doesn't
    // respect array boundaries when numcolumns > 1 and mobx will throw out of bounds errors
    return (
      // The ProductTileCarousel has 8px padding
      <ContentView testID="discover-screen">
        <FlatList
          testID="wishlist-screen-flatlist"
          key={`wishlist-flatlist-${numColumns}`}
          data={wishlistProducts || []}
          renderItem={renderItem}
          columnWrapperStyle={styles.columnWrapperStyle}
          numColumns={numColumns}
          showsVerticalScrollIndicator={isWebDesktopScreen}
          showsHorizontalScrollIndicator={isWebDesktopScreen}
          keyExtractor={(item, index) => `${item.id}-${index}`}
          ItemSeparatorComponent={() => (
            <View style={{marginTop: 8}} testID="wishlist-screen-flatlist-item-separator" />
          )}
          onScroll={scrollHandler}
          scrollEventThrottle={16}
          ref={listRef}
          contentContainerStyle={{...styles.contentContainer, paddingTop: headerHeight}}
          ListEmptyComponent={() =>
            isLoading ? (
              <LoadingImg />
            ) : (
              <View style={styles.centerNotice}>
                <Text
                  style={[
                    styles.largeNotice,
                    {
                      marginBottom: 16,
                    },
                  ]}>
                  NO PRODUCTS FOUND
                </Text>
                <Text
                  style={{
                    fontSize: 16,
                    fontWeight: '300',
                    color: theme.colour.text.heading.primary,
                    textAlign: 'center',
                    fontStyle: 'italic',
                  }}>
                  Some products in this wishlist may not be in stock at this store.
                </Text>
              </View>
            )
          }
        />
      </ContentView>
    );
  }
};

export default WishlistScreen;

const styles = StyleSheet.create({
  columnWrapperStyle: {gap: 8},
  centerNotice: {
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'stretch',
    marginTop: '50%',
  },
  largeNotice: {
    fontSize: 16,
    fontWeight: '500',
    color: theme.colour.text.heading.primary,
  },
  contentContainer: {
    ...Platform.select({
      ios: {},
      web: {
        flex: 1,
      },
    }),
  },
});
